// SPDX-License-Identifier: AGPL-3.0-or-later

import Catface from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-overlay-catface.svg?react';
import FrankensteinFace from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-overlay-frankenstein-face.svg?react';
import NurseHat from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-nurse.svg?react';
import CowboyHat from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-party-hat-cowboy.svg?react';
import JesterHat from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-party-hat-jester.svg?react';
import JesterPompomsHat from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-party-hat-jester-pompoms.svg?react';
import WizardHat from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-party-hat-wizard.svg?react';
import PirateHat from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-pirate-hat.svg?react';
import PirateHat2 from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-pirate-hat-2.svg?react';
import ThePanHat from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-the-pan.svg?react';
import WitchHat1 from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-witch-hat-1.svg?react';
import WitchHat3 from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-witch-hat-3.svg?react';
import WitchHat8 from '@app/features/holiday/packs/halloween/assets/avatar-decoration/avatar-topper-witch-hat-8.svg?react';
import BunnyEars from '@app/features/holiday/packs/halloween/assets/avatar-decoration/headband-bunny-ears.svg?react';
import DevilHorns from '@app/features/holiday/packs/halloween/assets/avatar-decoration/headband-devil-horns.svg?react';
import RingDevilHornTail from '@app/features/holiday/packs/halloween/assets/avatar-decoration/ring-devil-horn-tail.svg?react';
import RingWitchHat from '@app/features/holiday/packs/halloween/assets/avatar-decoration/ring-witch-hat.svg?react';
import {
	COSTUME_COLOR_DEFAULT_ID,
	COSTUME_COLOR_RANDOM_ID,
	COSTUME_DYES,
	COSTUME_SWATCH_IDS,
	type CostumeDyeOverrides,
	dyeById,
	normalizeCostumeColorId,
	paintsFromDye,
} from '@app/features/holiday/packs/halloween/costumeColors';
import {
	startCostumeRandomWindowTimer,
	stopCostumeRandomWindowTimer,
} from '@app/features/holiday/packs/halloween/costumeRandom';
import {
	HALLOWEEN_COSTUME_RANDOM_ID,
	type HalloweenCostumeId,
	isHalloweenCostumeId,
	parseCostumeChoice,
} from '@app/features/holiday/packs/halloween/halloweenCostumeIds';
import {isHolidayCategoryEnabled} from '@app/features/holiday/state/HolidayPreferences';
import {isHolidaySeasonEnabled} from '@app/features/holiday/utils/holidaySeason';
import {
	type AvatarCostumeColorOption,
	type AvatarCostumeDescriptor,
	type AvatarCostumePaints,
	type AvatarCostumeResolveInput,
	subscribeAvatarCostumeResolver,
} from '@app/features/ui/avatars/AvatarCostumeResolvers';
import Users from '@app/features/user/state/Users';
import {rollHalloweenCostumeColorId, rollHalloweenCostumeId} from '@fluxer/constants/src/HalloweenCostumeConstants';

const SILHOUETTE_COLOR = '#121414';
const HORNS_DEFAULT = '#860011';
const WITCH_HAT_2_DEFAULT = '#283519';
const WITCH_HAT_3_DEFAULT = '#1a679e';
const PURPLE_TRIM = '#f68228';
const BUNNY_WHITE = '#ffffff';
const BUNNY_PINK = '#f17db1';
const CATFACE_WHISKER = '#f5f5f5';
const HAT_BAND_WHITE = '#ffffff';
const WIZARD_BLUE = '#1e4fad';
const COWBOY_LEATHER = '#6b3a1f';
const COWBOY_GOLD = '#e8c547';
const JESTER_RED = '#c41e3a';
const JESTER_BLUE = '#1e4d9b';
const JESTER_POMPOMS_PURPLE = '#6b2d9b';
const JESTER_POMPOMS_GREEN = '#2e8240';
const JESTER_POMPOMS_YELLOW = '#f5d226';
const DYE_SECONDARY_BLACK = '#000000';
const WITCH_HAT_1_BAND_PURPLE = '#6b2d9b';
const WITCH_RING_DEFAULT_BODY = '#660000';
/** Orange hat bodies use the picker swatch, not the dark companion. */
const ORANGE_SWATCH = '#d35400';

function withOrangeHatBody(overrides: CostumeDyeOverrides = {}): CostumeDyeOverrides {
	return {
		...overrides,
		orange: {accentDark: ORANGE_SWATCH, ...overrides.orange},
	};
}

const WITCH_HAT_DYE_OVERRIDES: CostumeDyeOverrides = withOrangeHatBody({
	black: {accentSoft: HAT_BAND_WHITE},
	orange: {accentSoft: DYE_SECONDARY_BLACK},
	purple: {accentSoft: PURPLE_TRIM},
});

/** Cone + brim share the body slot. Band is light. Buckle is the pop (gold on Black). */
const WITCH_RING_DYE_OVERRIDES: CostumeDyeOverrides = withOrangeHatBody({
	black: {accent: COWBOY_GOLD, accentSoft: HAT_BAND_WHITE},
	orange: {accent: COWBOY_GOLD},
});

const WIZARD_HAT_DYE_OVERRIDES: CostumeDyeOverrides = withOrangeHatBody({
	black: {accent: COWBOY_GOLD},
	green: {accent: COWBOY_GOLD},
	orange: {accent: DYE_SECONDARY_BLACK},
	purple: {accent: PURPLE_TRIM},
});

const COWBOY_HAT_DYE_OVERRIDES: CostumeDyeOverrides = withOrangeHatBody({
	black: {accent: COWBOY_GOLD},
	orange: {accent: COWBOY_GOLD},
	purple: {accent: PURPLE_TRIM},
});

const PIRATE_SKULL_LIGHT = '#c5cad1';
const NURSE_CROSS_RED = '#ed1c24';
const NURSE_WHITE = '#ffffff';
const PAN_GREEN = '#639144';
const PAN_FEATHER = '#976d49';

const PIRATE_HAT_DYE_OVERRIDES: CostumeDyeOverrides = {
	black: {accent: DYE_SECONDARY_BLACK, accentDark: PIRATE_SKULL_LIGHT},
	orange: {accent: ORANGE_SWATCH, accentDark: DYE_SECONDARY_BLACK},
	purple: {accent: '#3d1860', accentDark: '#6b2d9b'},
	green: {accent: '#153f22', accentDark: '#246b38'},
	blood: {accent: '#860011', accentDark: '#c40014'},
};

/** Keep the red cross on Black; hat fill follows the dye (was locked white). */
const NURSE_HAT_DYE_OVERRIDES: CostumeDyeOverrides = {
	black: {accent: NURSE_CROSS_RED},
};

/**
 * Feather = accent. Outline/folds = accent-soft (black by default).
 * Orange→black feather; purple→pumpkin; green→gold; blood→pink; Black→gold outline.
 */
const PAN_HAT_DYE_OVERRIDES: CostumeDyeOverrides = withOrangeHatBody({
	black: {accent: PAN_FEATHER, accentSoft: COWBOY_GOLD},
	orange: {accent: DYE_SECONDARY_BLACK, accentSoft: SILHOUETTE_COLOR},
	purple: {accent: PURPLE_TRIM, accentSoft: SILHOUETTE_COLOR},
	green: {accent: COWBOY_GOLD, accentSoft: SILHOUETTE_COLOR},
	blood: {accent: '#c45a6e', accentSoft: SILHOUETTE_COLOR},
});

const BUNNY_DYE_OVERRIDES: CostumeDyeOverrides = {
	black: {accent: DYE_SECONDARY_BLACK},
	...Object.fromEntries(
		COSTUME_DYES.filter((dye) => dye.id !== 'black').map((dye) => [dye.id, {accentDark: SILHOUETTE_COLOR}]),
	),
} as CostumeDyeOverrides;

export type {
	HalloweenCostumeChoiceId,
	HalloweenCostumeId,
} from '@app/features/holiday/packs/halloween/halloweenCostumeIds';
export {
	DEFAULT_HALLOWEEN_COSTUME_ID,
	formatCostumeChoice,
	HALLOWEEN_COSTUME_CHOICES,
	HALLOWEEN_COSTUME_RANDOM_ID,
	isHalloweenCostumeChoiceId,
	isHalloweenCostumeId,
	parseCostumeChoice,
} from '@app/features/holiday/packs/halloween/halloweenCostumeIds';
export {COSTUME_COLOR_DEFAULT_ID, COSTUME_COLOR_RANDOM_ID, COSTUME_DYES, COSTUME_SWATCH_IDS, normalizeCostumeColorId};

/** Default color chip: four costume paints that are not dye swatches. */
export const COSTUME_DEFAULT_SWATCH_QUADS = [COWBOY_LEATHER, WIZARD_BLUE, JESTER_RED, COWBOY_GOLD] as const;

export const COSTUME_DEFAULT_SWATCH_BACKGROUND = `conic-gradient(from -90deg, ${COSTUME_DEFAULT_SWATCH_QUADS[0]} 0deg 90deg, ${COSTUME_DEFAULT_SWATCH_QUADS[1]} 90deg 180deg, ${COSTUME_DEFAULT_SWATCH_QUADS[2]} 180deg 270deg, ${COSTUME_DEFAULT_SWATCH_QUADS[3]} 270deg 360deg)`;

export type HalloweenCostumeCatalogEntry = AvatarCostumeDescriptor & {
	id: HalloweenCostumeId;
	/** Shared dye swatches (Default is synthetic in the picker). */
	colors: ReadonlyArray<AvatarCostumeColorOption>;
	/** Applied when wearer color is null / Default. */
	defaultPaints: AvatarCostumePaints;
	/** Optional per-dye slot patches; unspecified slots keep the shared dye. */
	dyeOverrides?: CostumeDyeOverrides;
};

/**
 * Art + placement per decoration. Numbers live on the entry.
 * Aspect ratios are one decimal. Placement nudges 2026-09-06: witch-hat-1 offsetX,
 * ring-witch-hat tighter, wizard/jester width, pirate offsetY.
 * Parked (not in this catalog): bunny-ears-flowers, ring-web, elf-ear.
 */
export const COSTUME_CATALOG: {[K in HalloweenCostumeId]: HalloweenCostumeCatalogEntry} = {
	'halloween.witch-hat-1': {
		id: 'halloween.witch-hat-1',
		placement: 'topper',
		art: 'accented',
		Svg: WitchHat1,
		accent: HAT_BAND_WHITE,
		accentDark: SILHOUETTE_COLOR,
		accentSoft: WITCH_HAT_1_BAND_PURPLE,
		haloFill: 'accentDark',
		widthRatio: 1.32,
		aspectRatio: 1.4,
		offsetXRatio: 0.02,
		offsetYRatio: -0.7,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: HAT_BAND_WHITE, accentDark: SILHOUETTE_COLOR, accentSoft: WITCH_HAT_1_BAND_PURPLE},
		dyeOverrides: WITCH_HAT_DYE_OVERRIDES,
	},
	'halloween.witch-hat-8': {
		id: 'halloween.witch-hat-8',
		placement: 'topper',
		art: 'silhouette',
		Svg: WitchHat8,
		tint: WITCH_HAT_2_DEFAULT,
		widthRatio: 1.24,
		aspectRatio: 1.2,
		offsetXRatio: 0,
		offsetYRatio: -0.55,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: WITCH_HAT_2_DEFAULT, accentDark: WITCH_HAT_2_DEFAULT},
		dyeOverrides: withOrangeHatBody(),
	},
	'halloween.witch-hat-3': {
		id: 'halloween.witch-hat-3',
		placement: 'topper',
		art: 'accented',
		Svg: WitchHat3,
		accent: HAT_BAND_WHITE,
		accentDark: WITCH_HAT_3_DEFAULT,
		accentSoft: HAT_BAND_WHITE,
		haloFill: 'accentDark',
		widthRatio: 1.24,
		aspectRatio: 1,
		offsetXRatio: 0,
		offsetYRatio: -0.7,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: HAT_BAND_WHITE, accentDark: WITCH_HAT_3_DEFAULT, accentSoft: HAT_BAND_WHITE},
		dyeOverrides: WITCH_HAT_DYE_OVERRIDES,
	},
	'halloween.ring-witch-hat': {
		id: 'halloween.ring-witch-hat',
		placement: 'ring',
		art: 'accented',
		Svg: RingWitchHat,
		accent: COWBOY_GOLD,
		accentDark: WITCH_RING_DEFAULT_BODY,
		accentSoft: SILHOUETTE_COLOR,
		haloFill: 'accentDark',
		widthRatio: 1.59,
		aspectRatio: 1,
		offsetXRatio: 0,
		offsetYRatio: -0.14,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: COWBOY_GOLD, accentDark: WITCH_RING_DEFAULT_BODY, accentSoft: SILHOUETTE_COLOR},
		dyeOverrides: WITCH_RING_DYE_OVERRIDES,
	},
	'halloween.party-hat-wizard': {
		id: 'halloween.party-hat-wizard',
		placement: 'topper',
		art: 'accented',
		Svg: WizardHat,
		accent: COWBOY_GOLD,
		accentDark: WIZARD_BLUE,
		haloFill: 'accentDark',
		widthRatio: 1.08,
		aspectRatio: 0.6,
		offsetXRatio: 0,
		offsetYRatio: -0.78,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: COWBOY_GOLD, accentDark: WIZARD_BLUE},
		dyeOverrides: WIZARD_HAT_DYE_OVERRIDES,
	},
	'halloween.party-hat-jester': {
		id: 'halloween.party-hat-jester',
		placement: 'topper',
		art: 'accented',
		Svg: JesterHat,
		accent: JESTER_RED,
		accentDark: JESTER_BLUE,
		widthRatio: 1.84,
		aspectRatio: 1.8,
		offsetXRatio: 0,
		offsetYRatio: -0.74,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: JESTER_RED, accentDark: JESTER_BLUE},
	},
	'halloween.party-hat-jester-pompoms': {
		id: 'halloween.party-hat-jester-pompoms',
		placement: 'topper',
		art: 'accented',
		Svg: JesterPompomsHat,
		accent: JESTER_POMPOMS_YELLOW,
		accentDark: JESTER_POMPOMS_PURPLE,
		accentSoft: JESTER_POMPOMS_GREEN,
		haloFill: 'accentDark',
		widthRatio: 1.82,
		aspectRatio: 1.7,
		offsetXRatio: 0,
		offsetYRatio: -0.76,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {
			accent: JESTER_POMPOMS_YELLOW,
			accentDark: JESTER_POMPOMS_PURPLE,
			accentSoft: JESTER_POMPOMS_GREEN,
		},
	},
	'halloween.party-hat-cowboy': {
		id: 'halloween.party-hat-cowboy',
		placement: 'topper',
		art: 'accented',
		Svg: CowboyHat,
		accent: COWBOY_GOLD,
		accentDark: COWBOY_LEATHER,
		haloFill: 'accentDark',
		widthRatio: 1.48,
		aspectRatio: 1.7,
		offsetXRatio: 0,
		offsetYRatio: -0.55,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: COWBOY_GOLD, accentDark: COWBOY_LEATHER},
		dyeOverrides: COWBOY_HAT_DYE_OVERRIDES,
	},
	'halloween.pirate-hat': {
		id: 'halloween.pirate-hat',
		placement: 'topper',
		art: 'accented',
		Svg: PirateHat,
		accent: SILHOUETTE_COLOR,
		accentDark: PIRATE_SKULL_LIGHT,
		haloFill: 'accent',
		widthRatio: 1.48,
		aspectRatio: 1.6,
		offsetXRatio: 0,
		offsetYRatio: -0.58,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: SILHOUETTE_COLOR, accentDark: PIRATE_SKULL_LIGHT},
		dyeOverrides: PIRATE_HAT_DYE_OVERRIDES,
	},
	'halloween.pirate-hat-2': {
		id: 'halloween.pirate-hat-2',
		placement: 'topper',
		art: 'accented',
		Svg: PirateHat2,
		accent: SILHOUETTE_COLOR,
		accentDark: PIRATE_SKULL_LIGHT,
		haloFill: 'accent',
		widthRatio: 1.48,
		aspectRatio: 2.4,
		offsetXRatio: 0,
		offsetYRatio: -0.58,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: SILHOUETTE_COLOR, accentDark: PIRATE_SKULL_LIGHT},
		dyeOverrides: PIRATE_HAT_DYE_OVERRIDES,
	},
	'halloween.nurse-hat': {
		id: 'halloween.nurse-hat',
		placement: 'topper',
		art: 'accented',
		Svg: NurseHat,
		accent: NURSE_CROSS_RED,
		accentDark: SILHOUETTE_COLOR,
		accentSoft: NURSE_WHITE,
		haloFill: 'accentDark',
		widthRatio: 1.08,
		aspectRatio: 1.4,
		offsetXRatio: 0,
		offsetYRatio: -0.73,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: NURSE_CROSS_RED, accentDark: SILHOUETTE_COLOR, accentSoft: NURSE_WHITE},
		dyeOverrides: NURSE_HAT_DYE_OVERRIDES,
	},
	'halloween.the-pan': {
		id: 'halloween.the-pan',
		placement: 'topper',
		art: 'accented',
		Svg: ThePanHat,
		accent: PAN_FEATHER,
		accentDark: PAN_GREEN,
		accentSoft: SILHOUETTE_COLOR,
		haloFill: 'accentDark',
		widthRatio: 1.5,
		aspectRatio: 1.7,
		offsetXRatio: -0.14,
		offsetYRatio: -0.55,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: PAN_FEATHER, accentDark: PAN_GREEN, accentSoft: SILHOUETTE_COLOR},
		dyeOverrides: PAN_HAT_DYE_OVERRIDES,
	},
	'halloween.devil-horns': {
		id: 'halloween.devil-horns',
		placement: 'ring',
		art: 'accented',
		Svg: DevilHorns,
		accent: HORNS_DEFAULT,
		accentDark: DYE_SECONDARY_BLACK,
		haloFill: 'accent',
		widthRatio: 1.06,
		aspectRatio: 0.8,
		offsetXRatio: 0,
		offsetYRatio: -0.17,
		colors: COSTUME_DYES,
		defaultPaints: {accent: HORNS_DEFAULT, accentDark: DYE_SECONDARY_BLACK},
	},
	'halloween.bunny-ears': {
		id: 'halloween.bunny-ears',
		placement: 'ring',
		art: 'accented',
		Svg: BunnyEars,
		accent: BUNNY_WHITE,
		accentDark: SILHOUETTE_COLOR,
		accentSoft: BUNNY_PINK,
		widthRatio: 1.8,
		aspectRatio: 0.7,
		offsetXRatio: -0.09,
		offsetYRatio: -0.313,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: BUNNY_WHITE, accentDark: SILHOUETTE_COLOR, accentSoft: BUNNY_PINK},
		dyeOverrides: BUNNY_DYE_OVERRIDES,
	},
	'halloween.ring-devil-horn-tail': {
		id: 'halloween.ring-devil-horn-tail',
		placement: 'ring',
		art: 'accented',
		Svg: RingDevilHornTail,
		accent: HORNS_DEFAULT,
		accentDark: DYE_SECONDARY_BLACK,
		haloFill: 'accent',
		partHalo: true,
		widthRatio: 1.55,
		aspectRatio: 1,
		offsetXRatio: -0.067,
		offsetYRatio: -0.126,
		scaleAboveAvatar: true,
		colors: COSTUME_DYES,
		defaultPaints: {accent: HORNS_DEFAULT, accentDark: DYE_SECONDARY_BLACK},
	},
	'halloween.catface': {
		id: 'halloween.catface',
		placement: 'face-overlay',
		art: 'accented',
		Svg: Catface,
		accent: CATFACE_WHISKER,
		accentDark: SILHOUETTE_COLOR,
		partHalo: true,
		widthRatio: 1.85,
		aspectRatio: 1.7,
		offsetXRatio: 0,
		offsetYRatio: -0.1,
		colors: COSTUME_DYES,
		defaultPaints: {accent: CATFACE_WHISKER, accentDark: SILHOUETTE_COLOR},
	},
	'halloween.frankenstein-face': {
		id: 'halloween.frankenstein-face',
		placement: 'face-overlay',
		art: 'accented',
		Svg: FrankensteinFace,
		accent: SILHOUETTE_COLOR,
		accentDark: SILHOUETTE_COLOR,
		haloFill: 'accentDark',
		widthRatio: 1.28,
		aspectRatio: 1,
		offsetXRatio: 0,
		offsetYRatio: 0.02,
		colors: COSTUME_DYES,
		defaultPaints: {accent: SILHOUETTE_COLOR, accentDark: SILHOUETTE_COLOR},
	},
};

export {rollHalloweenCostumeColorId, rollHalloweenCostumeId};

/** Unpicked users wear nothing, including to themselves. Randomize is opt-in, not a default. */
export function resolveHalloweenCostumeId(userId: string, now = Date.now()): HalloweenCostumeId | null {
	const {baseId} = parseCostumeChoice(Users.getUser(userId)?.avatarCostumeId);
	if (baseId === HALLOWEEN_COSTUME_RANDOM_ID) return rollHalloweenCostumeId(userId, now);
	if (isHalloweenCostumeId(baseId)) return baseId;
	return null;
}

export function resolveHalloweenCostumeFlipped(userId: string): boolean {
	return parseCostumeChoice(Users.getUser(userId)?.avatarCostumeId).flipped;
}

function resolveCostumeIdForUser(userId: string): HalloweenCostumeId | null {
	return resolveHalloweenCostumeId(userId);
}

/**
 * Returns a dye option, or `null` for Default (use `entry.defaultPaints`).
 * Randomize is not a dye — resolve via `resolveCostumeColorForUser`.
 */
export function resolveCostumeColorOption(
	entry: HalloweenCostumeCatalogEntry,
	colorId: string | null | undefined,
): AvatarCostumeColorOption | null {
	const normalized = normalizeCostumeColorId(colorId);
	if (normalized == null || normalized === COSTUME_COLOR_RANDOM_ID) return null;
	return entry.colors.find((option) => option.id === normalized) ?? dyeById(normalized) ?? null;
}

export function resolveCostumeColorForUser(userId: string, now = Date.now()): AvatarCostumeColorOption | null {
	const stored = Users.getUser(userId)?.avatarCostumeColor;
	if (stored == null || stored === COSTUME_COLOR_RANDOM_ID) {
		const rolled = rollHalloweenCostumeColorId(userId, now);
		if (rolled == null) return null;
		return dyeById(rolled) ?? null;
	}
	const normalized = normalizeCostumeColorId(stored);
	if (normalized === COSTUME_COLOR_RANDOM_ID) {
		const rolled = rollHalloweenCostumeColorId(userId, now);
		if (rolled == null) return null;
		return dyeById(rolled) ?? null;
	}
	if (normalized == null) return null;
	return dyeById(normalized) ?? null;
}

/** Body fill for swatch chips. Silhouettes and `haloFill: 'accentDark'` use the dark slot. */
export function costumePrimaryFill(
	entry: HalloweenCostumeCatalogEntry,
	color: AvatarCostumeColorOption | null,
): string {
	const paints: AvatarCostumePaints = color == null ? entry.defaultPaints : paintsFromDye(color, entry.dyeOverrides);
	if (entry.art === 'silhouette' || ('haloFill' in entry && entry.haloFill === 'accentDark')) return paints.accentDark;
	return paints.accent;
}

function withFlip<T extends AvatarCostumeDescriptor>(descriptor: T, flipped: boolean): T {
	if (!flipped) return descriptor;
	return {
		...descriptor,
		flipHorizontal: true,
		offsetXRatio: -descriptor.offsetXRatio || 0,
	};
}

export function applyCostumeColor(
	entry: HalloweenCostumeCatalogEntry,
	color: AvatarCostumeColorOption | null,
	flipped = false,
): AvatarCostumeDescriptor {
	const {colors: _colors, defaultPaints, dyeOverrides, ...descriptor} = entry;
	const paints: AvatarCostumePaints = color == null ? defaultPaints : paintsFromDye(color, dyeOverrides);
	if (descriptor.art === 'accented') {
		return withFlip(
			{
				...descriptor,
				accent: paints.accent,
				accentDark: paints.accentDark,
				accentSoft: paints.accentSoft,
			},
			flipped,
		);
	}
	if (descriptor.art === 'silhouette') {
		// Body slot, same as accented hats — not the second-tone `accent`.
		return withFlip({...descriptor, tint: paints.accentDark}, flipped);
	}
	return withFlip(descriptor, flipped);
}

/** DEV-only. Point at one catalog id while placing art. Production builds never force. */
const DEBUG_FORCE_COSTUME_ID: HalloweenCostumeId | null = null;

export function resolveHalloweenAvatarCostume({userId}: AvatarCostumeResolveInput): AvatarCostumeDescriptor | null {
	if (import.meta.env.DEV && DEBUG_FORCE_COSTUME_ID != null) {
		const forced = COSTUME_CATALOG[DEBUG_FORCE_COSTUME_ID];
		return applyCostumeColor(forced, resolveCostumeColorOption(forced, null));
	}
	if (!isHolidayCategoryEnabled('avatarCostumes', 'halloween')) return null;
	if (!isHolidaySeasonEnabled('halloween')) return null;
	const costumeId = resolveCostumeIdForUser(userId);
	if (costumeId == null) return null;
	const entry = COSTUME_CATALOG[costumeId];
	if (entry == null) return null;
	const color = resolveCostumeColorForUser(userId);
	return applyCostumeColor(entry, color, resolveHalloweenCostumeFlipped(userId));
}

let halloweenAvatarCostumesRegistered = false;
let unsubscribeHalloweenAvatarCostumes: (() => void) | null = null;

function disposeHalloweenAvatarCostumes(): void {
	unsubscribeHalloweenAvatarCostumes?.();
	unsubscribeHalloweenAvatarCostumes = null;
	stopCostumeRandomWindowTimer();
	halloweenAvatarCostumesRegistered = false;
}

export function registerHalloweenAvatarCostumes(): void {
	if (halloweenAvatarCostumesRegistered) return;
	halloweenAvatarCostumesRegistered = true;
	unsubscribeHalloweenAvatarCostumes = subscribeAvatarCostumeResolver(resolveHalloweenAvatarCostume);
	startCostumeRandomWindowTimer();
}

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		disposeHalloweenAvatarCostumes();
	});
}
